import React, { useCallback } from "react";

import { isEmpty } from "lodash";
import {
  Control,
  useFieldArray,
  useForm,
  useFormState,
} from "react-hook-form-7";
import { Link } from "react-router-dom";

import {
  Box,
  Button,
  Container,
  Flex,
  Text,
  Stack,
  StackDivider,
} from "@chakra-ui/react";

import useTranslations from "../../../hooks/useTranslations";
import useNotification from "../../../hooks/useNotification";
import useStore from "../../../store";
import useConfirm from "../../utils/confirm/useConfirm";

import {
  AnswerInput,
  ReportFragment,
  useFillReferentReport,
  useFillReport,
  useUpdateReport,
} from "../../../graphql/generated/definitions";
import { ReportFormValues } from "../types/ReportFormTypes";

import ReportLayout from "../layout/ReportLayout";
import SectionNavigationHeader from "../components/sections/SectionNavigationHeader";
import ReportDownloadAsPDF from "../components/ReportDownloadAsPDF";
import ReportFormHeader from "../components/ReportFormHeader";
import SectionForm from "../components/sections/SectionForm";

import {
  coerceToAnswerValue,
  getAnswerDefaultValue,
  getAnswerValue,
  isAnswerEmpty,
} from "../utils/AnswerValueHelpers";

function ReportFormSubmitPanel({
  control,
}: {
  control: Control<ReportFormValues>;
}) {
  const t = useTranslations();
  const { isSubmitting } = useFormState({ control });
  const isSubmittable = useStore((state) => state.report.isSubmittable);
  const textInputFocused = useStore((state) => state.report.textInputFocused);

  return (
    <Box
      py="3"
      bottom={0}
      w="full"
      bgColor="white"
      position="fixed"
      borderTop="1px"
      borderColor="gray.200"
      display={[textInputFocused ? "none" : "block", "block"]}
    >
      <Flex justify="center">
        <Button
          type="submit"
          colorScheme="primary"
          variant="solid"
          size="sm"
          id="submit-report-form"
          isLoading={isSubmitting}
          isDisabled={!isSubmittable}
        >
          {t("reports.edit.submitAnswers")}
        </Button>
      </Flex>
    </Box>
  );
}

export default function EditPage({ report }: { report: ReportFragment }) {
  const t = useTranslations();
  const confirm = useConfirm();

  const viewer = useStore((state) => state.viewer);
  const [viewerRole, reportState] = useStore(
    (state) => state.report.viewedReportState
  );
  const setCurrentStep = useStore((state) => state.report.setCurrentStep);
  const sections = useStore((state) => state.report.sections);

  const { notifyError } = useNotification();
  const failureNotification = useCallback((error) => {
    let message = t("reports.notification.error");

    if (error?.toString().match(/Expected value to not be null/)) {
      message = t("reports.notification.notNull");
    }

    notifyError({
      title: message as string,
    });
  }, []);

  const { handleSubmit, control, ...formMethods } = useForm<ReportFormValues>({
    defaultValues: {
      sections: sections?.map(({ questions }) => ({
        answers: questions.map(({ answers, previousAnswers, type }) => {
          const draftAnswer = answers.find(
            (answer) => answer.user.id === viewer?.id && answer.isDraft
          );

          const previousAnswer = previousAnswers.find(
            (previousAnswer) => previousAnswer.user.id === viewer?.id
          );

          if (draftAnswer?.value)
            return {
              value: !isEmpty(draftAnswer?.value)
                ? getAnswerValue(draftAnswer)
                : getAnswerDefaultValue(type),
              comment: draftAnswer?.value?.comment || "",
              timeSpent: draftAnswer?.timeSpent?.toString() || "",
            };

          if (
            previousAnswer?.value &&
            (previousAnswer?.type === "AnswerEvaluation" ||
              previousAnswer?.type === "AnswerSkill")
          )
            return {
              value: !isEmpty(previousAnswer?.value)
                ? getAnswerValue(previousAnswer)
                : getAnswerDefaultValue(type),
              // We don't want the comment to be reported from the previous answer
              comment: "",
              timeSpent: "",
            };
        }),
      })),
    },
  });

  const template = report.template || report.review.template;

  const { fields: sectionFields, ...fieldArrayMethods } = useFieldArray({
    control,
    name: "sections",
    keyName: "_sectionFieldId",
  });

  const [updateReport] = useUpdateReport();
  const [fillReport] = useFillReport();
  const [fillReferentReport] = useFillReferentReport();

  const onSubmit = useCallback(
    async (data: ReportFormValues) => {
      if (reportState === "edit") {
        confirm({
          title: t("reports.edit.confirmSubmitTitle"),
          message: t(`reports.edit.confirmSubmitDescription`),
          buttons: [
            { text: t("forms.actions.cancel") },
            {
              colorScheme: "green",
              id: "submit-report-confirm",
              text: t("forms.actions.send"),
              onClick: async () => {
                try {
                  await updateReport({
                    variables: {
                      input: {
                        id: report.id,
                        answersAttributes: data.sections.flatMap(
                          ({ answers }) =>
                            answers
                              .map(
                                ({
                                  value,
                                  comment,
                                  timeSpent,
                                  type,
                                  id,
                                  questionId,
                                }): AnswerInput => ({
                                  id: id,
                                  questionId: questionId,

                                  isDraft: false,
                                  timeSpent: timeSpent
                                    ? parseInt(timeSpent)
                                    : null,
                                  type: type,
                                  userId: viewer.id,
                                  collaboratorId: report.review.collaboratorId,
                                  value: isAnswerEmpty({ value, comment, type })
                                    ? null
                                    : coerceToAnswerValue({
                                        value,
                                        comment,
                                        type,
                                      }),
                                })
                              )
                              .filter(({ value }) => !isEmpty(value))
                        ),
                      },
                    },
                  });
                  if (viewerRole === "collaborator")
                    await fillReport({
                      variables: { input: { id: report.id } },
                    });

                  if (viewerRole === "referent")
                    await fillReferentReport({
                      variables: { input: { id: report.id } },
                    });

                  setCurrentStep("success");
                } catch (e) {
                  failureNotification(e);
                }
              },
            },
          ],
        });
      }
    },
    [viewerRole, reportState, report]
  );

  return (
    <ReportLayout subHeader={<SectionNavigationHeader sections={sections} />}>
      <Flex
        direction="column"
        flexGrow={1}
        bgColor="primary.50"
        py={["0", "10"]}
        pb={["0", "24"]}
        overflowY="auto"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Container
            shadow="base"
            rounded={["none", "md"]}
            px="0"
            mb={["12", "0"]}
            maxW="container.xl"
            bgColor="white"
          >
            <ReportFormHeader report={report} />
            <Flex
              direction="column"
              roundedBottom="md"
              py={["10", "16"]}
              px={["6", "16"]}
            >
              <Text
                fontSize={["2xl", "4xl"]}
                fontWeight="bold"
                color="brand"
              >
                {t("reports.edit.startOfQuestionnaire")}
              </Text>
              <Flex experimental_spaceX="4" pt="3">
                {template.canInsert &&
                  report.canUpdate &&
                  report.review.allowTemplateUpdateByCollaborator &&
                  viewer.company.inlineTemplateUpdateEnabled && (
                    <Button
                      as={Link}
                      id="edit-template-action"
                      to={`/templates/${report.review.templateId}/edit?reportId=${report.id}&reviewId=${report.review.id}`}
                      size="sm"
                      variant="outline"
                      color="darkBrand"
                    >
                      {t("reports.edit.editQuestionnaire")}
                    </Button>
                  )}
                <ReportDownloadAsPDF report={report} />
              </Flex>
              <Stack
                direction="column"
                spacing="16"
                pt="16"
                divider={<StackDivider borderColor="gray.100" />}
              >
                {sectionFields?.map((section, index) => (
                  <SectionForm
                    key={section._sectionFieldId}
                    report={report}
                    sectionIndex={index}
                    path={`sections.${index}` as const}
                    section={sections[index]}
                    {...{
                      control,
                      ...formMethods,
                      ...fieldArrayMethods,
                    }}
                  />
                ))}
              </Stack>
            </Flex>
          </Container>
          <ReportFormSubmitPanel control={control} />
        </form>
      </Flex>
    </ReportLayout>
  );
}
